// skipcq: JS-W1028
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../utils/firebase';
import Snackbar from '@mui/material/Snackbar';
import Alert from '../../components/widgets/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useBrewery from '../../hooks/useBrewery';
import useBeerReviewCount from '../../hooks/useBeerReviewCount';
import useCheckinsCount from '../../hooks/useCheckinsCount';
import sanitizedUrl from '../../utils/sanitizeURL';

// Renders a single fixed-height row for alignment across columns
function SummaryRow({ children }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 32,
        mb: 0.5
      }}>
      {children}
    </Box>
  );
}

// Read-only brewery group brand context for merge decisions (global-admin Firestore reads).
function MergeBreweryGroupContextLine({ groupIdCaptured }) {
  const normalizedGroupIdConstructed =
    typeof groupIdCaptured === 'string' ? groupIdCaptured.trim() : '';
  const [groupLabelConstructed, setGroupLabelConstructed] = useState('');

  useEffect(() => {
    let canceledFlagConstructed = false;

    async function loadBrandLabelDescribe() {
      setGroupLabelConstructed('');

      if (!normalizedGroupIdConstructed) {
        return;
      }

      try {
        const snapConstructed = await getDoc(
          doc(db, 'breweryGroups', normalizedGroupIdConstructed),
        );
        const parsedDataConstructed = snapConstructed.data() ?? {};
        const brandNameConstructed =
          typeof parsedDataConstructed.displayName === 'string'

            ? parsedDataConstructed.displayName.trim()

            : '';
        const nextLabelConstructed =
          brandNameConstructed.length > 0
            ? `${brandNameConstructed} (${normalizedGroupIdConstructed})`
            : normalizedGroupIdConstructed;

        if (!canceledFlagConstructed) {

          setGroupLabelConstructed(nextLabelConstructed);

        }

      } catch {

        if (!canceledFlagConstructed) {

          setGroupLabelConstructed(normalizedGroupIdConstructed);

        }

      }
    }

    loadBrandLabelDescribe();

    return () => {

      canceledFlagConstructed = true;

    };

  }, [normalizedGroupIdConstructed]);

  return (
    <Box sx={{
      mt: 1,

    }}

    >
      <SummaryRow>
        <Typography variant="body2" color="text.secondary">

          Brewery group (survivor keeps its group):{' '}

          {!normalizedGroupIdConstructed

            ? 'none'

            : groupLabelConstructed || 'Loading…'}

        </Typography>

      </SummaryRow>

    </Box>

  );

}

// Renders a compact summary for a brewery with name and address
function BrewerySummary({ brewery, breweryId }) {
  // Call hooks unconditionally to follow Rules of Hooks
  const { count: beerReviewCount } = useBeerReviewCount(breweryId);
  const { count: checkinsCount } = useCheckinsCount(breweryId);

  if (!brewery) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "120px"
        }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const { name, address1, city, state, zip, website, hospitalityRatings } =
    brewery;
  const hospitalityReviewCount = hospitalityRatings?.qualityRating?.count ?? 0;

  return (
    <Box>
      <SummaryRow>
        <Typography variant="h6">{name || '—'}</Typography>
      </SummaryRow>
      <SummaryRow>
        <Typography variant="body2">{address1 || ' '}</Typography>
      </SummaryRow>
      <SummaryRow>
        <Typography variant="body2">
          {[city, state, zip].filter(Boolean).join(' ') || ' '}
        </Typography>
      </SummaryRow>
      <SummaryRow>
        {website ? (
          <Typography variant="body2">
            <Link
              href={sanitizedUrl(website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {website}
            </Link>
          </Typography>
        ) : (
          <Typography variant="body2"> </Typography>
        )}
      </SummaryRow>
      <Box sx={{
        mt: 1
      }}>
        <SummaryRow>
          <Typography variant="body2">
            Hospitality reviews: {hospitalityReviewCount}
          </Typography>
        </SummaryRow>
      </Box>
      <Box sx={{
        mt: 1
      }}>
        <SummaryRow>
          <Typography variant="body2">
            Beer reviews: {beerReviewCount}
          </Typography>
        </SummaryRow>
      </Box>
      <Box sx={{
        mt: 1
      }}>
        <SummaryRow>
          <Typography variant="body2">Log entries: {checkinsCount}</Typography>
        </SummaryRow>
      </Box>
      <MergeBreweryGroupContextLine groupIdCaptured={brewery.breweryGroupId} />
    </Box>
  );
}

// Displays two breweries side-by-side (50/50) for merge preparation
function MergeBreweries() {
  const navigate = useNavigate();
  const { idA, idB } = useParams();

  const breweryA = useBrewery(idA);
  const breweryB = useBrewery(idB);

  const [survivingId, setSurvivingId] = useState(idA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDryRunOpen, setIsDryRunOpen] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [submittingAction, setSubmittingAction] = useState(null); // 'dryRun' | 'merge' | null

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSurvivorChange = useCallback((event) => {
    setSurvivingId(event.target.value);
  }, []);

  const executeMerge = useCallback(
    async (dryRun) => {
      if (
        !idA ||
        !idB ||
        !survivingId ||
        (survivingId !== idA && survivingId !== idB)
      ) {
        setSubmittingAction(null);
        return;
      }
      if (idA === idB) {
        setErrorMessage('Both selected brewery IDs are identical');
        setIsErrorOpen(true);
        setSubmittingAction(null);
        return;
      }
      try {
        setIsSubmitting(true);
        const mergeBreweries = httpsCallable(
          functions,
          'callableBreweryMergeBreweries-mergeBreweries',
        );
        const res = await mergeBreweries({
          breweryIdA: idA,
          breweryIdB: idB,
          newParentBreweryId: survivingId,
          dryRun,
        });
        // eslint-disable-next-line no-console
        console.log('Merge response', res?.data);
        if (dryRun) {
          setDryRunResult(res?.data || null);
          setIsDryRunOpen(true);
        } else {
          setConfirmResult(res?.data || null);
          setIsConfirmOpen(true);
          setIsSuccessOpen(true);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Merge error', err);
        setErrorMessage(err?.message || 'Merge failed');
        setIsErrorOpen(true);
      } finally {
        setIsSubmitting(false);
        setSubmittingAction(null);
      }
    },
    [idA, idB, survivingId],
  );

  const handleDryRunClick = useCallback(() => {
    setSubmittingAction('dryRun');
    executeMerge(true);
  }, [executeMerge]);

  const handleMergeClick = useCallback(() => {
    setSubmittingAction('merge');
    executeMerge(false);
  }, [executeMerge]);

  const handleCloseDryRun = useCallback(() => {
    setIsDryRunOpen(false);
  }, []);

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleCloseSuccess = useCallback(() => {
    setIsSuccessOpen(false);
  }, []);

  const handleGoToBrewery = useCallback(() => {
    const id = confirmResult?.mergedIntoBreweryId || survivingId;
    if (id) {
      navigate(`/brewery/${id}`);
    }
  }, [confirmResult, survivingId, navigate]);

  const handleCloseError = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsErrorOpen(false);
  }, []);

  return (
    // skipcq: JS-0415
    <DashboardLayout title="Merge Breweries">
      <Box sx={{
        p: 2
      }}>
        <Box sx={{
          mb: 2
        }}>
          <Button variant="contained" onClick={handleBack}>
            Back
          </Button>
        </Box>
        <Box sx={{
          mb: 2
        }}>
          <FormControl fullWidth>
            <FormLabel>Surviving Brewery</FormLabel>
            <RadioGroup
              name="surviving-id"
              value={survivingId}
              onChange={handleSurvivorChange}
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: 2,
                rowGap: 1,
              }}
            >
              <FormControlLabel
                value={idA}
                control={<Radio />}
                label={`${breweryA?.name ?? 'ID A'} (${idA})`}
              />
              <FormControlLabel
                value={idB}
                control={<Radio />}
                label={`${breweryB?.name ?? 'ID B'} (${idB})`}
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Grid container spacing={2} sx={{
          alignItems: "flex-start"
        }}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                minHeight: "200px"
              }}>
              <BrewerySummary brewery={breweryA} breweryId={idA} />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                minHeight: "200px"
              }}>
              <BrewerySummary brewery={breweryB} breweryId={idB} />
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1
          }}>
          <Button
            variant="outlined"
            onClick={handleDryRunClick}
            disabled={
              isSubmitting ||
              !survivingId ||
              (survivingId !== idA && survivingId !== idB)
            }
            startIcon={
              submittingAction === 'dryRun' && isSubmitting ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            Dry Run
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleMergeClick}
            disabled={
              isSubmitting ||
              !survivingId ||
              (survivingId !== idA && survivingId !== idB)
            }
            startIcon={
              submittingAction === 'merge' && isSubmitting ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            Merge
          </Button>
        </Box>
      </Box>
      <Dialog
        open={isDryRunOpen}
        onClose={handleCloseDryRun}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Dry Run Results</DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            mb: 1
          }}>
            <Typography variant="body2">
              Items that will be merged into{' '}
              {dryRunResult?.mergedIntoBreweryId ?? '—'}. Logged with
              correlation ID: {dryRunResult?.correlationId || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              Company Locations: {dryRunResult?.counts?.companyLocations ?? 0}
            </Typography>
            <Typography variant="body2">
              Hospitality Reviews:{' '}
              {dryRunResult?.counts?.hospitalityReviews ?? 0}
            </Typography>
            <Typography variant="body2">
              Beer Reviews: {dryRunResult?.counts?.beerReviews ?? 0}
            </Typography>
            <Typography variant="body2">
              Log entries: {dryRunResult?.counts?.checkIns ?? 0}
            </Typography>
            <Typography variant="body2">
              User Favs Docs: {dryRunResult?.counts?.userFavsDocs ?? 0}
            </Typography>
            <Typography variant="body2">
              Total Estimated Writes:{' '}
              {dryRunResult?.counts?.totalEstimatedWrites ?? 0}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseDryRun}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isConfirmOpen}
        onClose={handleCloseConfirm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Merge Completed</DialogTitle>
        <DialogContent dividers>
          <Box sx={{
            mb: 1
          }}>
            <Typography variant="body2">
              Merged into brewery: {confirmResult?.mergedIntoBreweryId || '—'}
            </Typography>
            <Typography variant="body2">
              Correlation ID: {confirmResult?.correlationId || '—'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseConfirm}>
            Close
          </Button>
          <Button variant="contained" onClick={handleGoToBrewery}>
            Go to brewery
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={isSuccessOpen}
        autoHideDuration={4000}
        onClose={handleCloseSuccess}
      >
        <Alert
          onClose={handleCloseSuccess}
          severity="success"
          sx={{ width: '100%' }}
        >
          Merge completed successfully
        </Alert>
      </Snackbar>
      <Snackbar open={isErrorOpen} onClose={handleCloseError}>
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage || 'An error occurred'}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default MergeBreweries;
